// Cmd+K ask palette (spec 16 §7).
//
// Centered overlay that's both the entry point and the answer surface.
// The same <Canvas /> renders inside; recents appear in the empty
// state; a running ask shows phase next to the mark and offers a
// Cancel affordance.
//
// Keyboard:
//   ⌘K        — toggle (handled in AppShell)
//   ↩         — submit
//   ⌘↩        — open completed ask in deep view
//   esc       — close
//
// Closes via: esc / backdrop click / Close button in the footer.

import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { ArrowLeft, CornerDownRight, Maximize2 } from "lucide-react";

import { Canvas } from "@/components/canvas/canvas";
import {
	type AskResult,
	type CanvasPlan,
	defaultCanvas,
	validateCanvas,
} from "@/components/canvas/types";
import { Mark, type MarkState } from "@/components/mark";

/**
 * State seeded into the palette from another surface (e.g. the /ask
 * thread page's "Continue the thread" action). The composer opens
 * empty, but submitting carries `priorTurns` as conversational context
 * and a small "Following up on" indicator points back at the source.
 */
export interface PaletteSeed {
	priorTurns: { question: string; answer: string }[];
	branchedFrom?: { question: string; jobId: string | null };
}

interface AskPaletteProps {
	open: boolean;
	onClose: () => void;
	seed?: PaletteSeed | null;
}

interface JobSnapshot {
	id: string;
	status: "pending" | "running" | "completed" | "failed" | "cancelled";
	progress?: { phase?: string; processed?: number; total?: number };
	result?: unknown;
	error?: string | null;
	type?: string;
	input?: { question?: string };
	createdAt?: string;
}

interface RecentAsk {
	id: string;
	question: string;
	createdAt: string;
}

const PHASE_LABEL: Record<string, string> = {
	pending: "Queued",
	embedding: "Embedding your question",
	vector_search: "Searching the corpus",
	expanding: "Expanding the neighborhood",
	answering: "Drafting an answer",
	ranking: "Ranking communities",
	map: "Reading community reports",
	reduce: "Synthesizing",
	composing: "Composing the canvas",
};

const SUGGESTIONS = [
	"Summarize what this corpus is about.",
	"What are the biggest open questions?",
	"What changed recently?",
];

export function AskPalette({ open, onClose, seed }: AskPaletteProps) {
	const { id: projectId } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const inputRef = useRef<HTMLTextAreaElement | null>(null);
	const sseRef = useRef<EventSource | null>(null);

	const [question, setQuestion] = useState("");
	const [submitted, setSubmitted] = useState<string | null>(null);
	const [phase, setPhase] = useState<string | null>(null);
	const [jobId, setJobId] = useState<string | null>(null);
	const [result, setResult] = useState<AskResult | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [recents, setRecents] = useState<RecentAsk[]>([]);
	/**
	 * Spec 16 light threading. Holds completed turns from this palette
	 * session so a follow-up ask can pass them as conversational
	 * context. Capped at 3 (server also enforces). Cleared on
	 * back-to-main and on close.
	 */
	const [priorTurns, setPriorTurns] = useState<
		{ question: string; answer: string }[]
	>([]);
	/**
	 * Mirror of seed.branchedFrom for in-palette continuations. When
	 * the user clicks "Continue the thread" from the post-answer
	 * canvas, we drop back to the empty composer with priorTurns
	 * loaded and this state set, which surfaces the "Following up on"
	 * indicator above the composer just like a seeded open would.
	 */
	const [inThreadFrom, setInThreadFrom] = useState<{
		question: string;
	} | null>(null);

	// Focus the composer on open; tear down stream on close.
	useEffect(() => {
		if (open) {
			const t = setTimeout(() => inputRef.current?.focus(), 60);
			return () => clearTimeout(t);
		}
		sseRef.current?.close();
		sseRef.current = null;
	}, [open]);

	// Hydrate from seed when the palette opens with one. A seeded open
	// is a FRESH composer with thread context — not a continuation of
	// whatever ask was on screen the last time the palette was open.
	// So we hydrate priorTurns AND drop any stale per-ask state, which
	// puts the user in the empty-composer state with the
	// "Following up on" indicator visible.
	useEffect(() => {
		if (!open || !seed?.priorTurns) return;
		setPriorTurns(seed.priorTurns.slice(-3));
		if (seed.branchedFrom) {
			setInThreadFrom({ question: seed.branchedFrom.question });
		}
		sseRef.current?.close();
		sseRef.current = null;
		setSubmitted(null);
		setResult(null);
		setPhase(null);
		setJobId(null);
		setError(null);
		setQuestion("");
		setTimeout(() => inputRef.current?.focus(), 60);
	}, [open, seed]);

	// Load recent asks for this project (empty state only).
	useEffect(() => {
		if (!open || !projectId || submitted) return;
		let cancelled = false;
		fetch(`/api/projects/${encodeURIComponent(projectId)}/jobs?limit=80`, {
			credentials: "include",
		})
			.then((r) => (r.ok ? r.json() : null))
			.then((body) => {
				if (cancelled || !body) return;
				const all = (body.jobs ?? []) as JobSnapshot[];
				const asks: RecentAsk[] = [];
				for (const j of all) {
					if (j.type !== "ask") continue;
					const q = j.input?.question;
					if (!q) continue;
					asks.push({
						id: j.id,
						question: q,
						createdAt: j.createdAt ?? "",
					});
					if (asks.length >= 6) break;
				}
				setRecents(asks);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
	}, [open, projectId, submitted]);

	// Smart Esc: when an answer / running ask is on screen, the FIRST
	// Esc backs out to the main menu (recents + composer). A second
	// Esc closes the palette entirely. This is the Raycast / VS Code
	// pattern; users get a way to recover from a stale answer without
	// losing the whole modal context.
	useEffect(() => {
		if (!open) return;
		const handler = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				e.preventDefault();
				if (submitted) {
					reset();
				} else {
					onClose();
				}
			}
		};
		window.addEventListener("keydown", handler);
		return () => window.removeEventListener("keydown", handler);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, onClose, submitted]);

	void jobId;
	void result;

	// Source / entity click → close palette and focus the node in the
	// graph view. Spec 16-equivalent: the cards aren't decoration,
	// they're the entry point into the underlying KB.
	function focusNodeInGraph(nodeId: string) {
		if (!projectId) return;
		onClose();
		const sp = new URLSearchParams({ focus: nodeId });
		navigate(`/projects/${projectId}/graph?${sp.toString()}`);
	}

	/**
	 * Expand: navigate to the full-page ask route, anchored to the
	 * current job. The job's `input.prior_turns` already carries the
	 * thread (we sent it server-side when submitting), so the route
	 * reconstructs the conversation from that single source of truth.
	 * No client-side state handoff.
	 */
	function expandToRoute() {
		if (!projectId || !jobId) return;
		onClose();
		const sp = new URLSearchParams({ job: jobId });
		navigate(`/projects/${projectId}/ask?${sp.toString()}`);
	}

	async function submit(qOverride?: string) {
		if (!projectId) return;
		const q = (qOverride ?? question).trim();
		if (q.length < 2) return;

		// If we have a completed prior turn, fold it into the thread
		// stack so the next ask carries Q+A as conversational context.
		// Cap at 3 so prompt growth stays bounded (server also caps).
		let nextPriorTurns = priorTurns;
		if (submitted && result?.answer) {
			nextPriorTurns = [
				...priorTurns,
				{ question: submitted, answer: result.answer },
			].slice(-3);
			setPriorTurns(nextPriorTurns);
		}

		sseRef.current?.close();
		sseRef.current = null;
		setSubmitted(q);
		setResult(null);
		setError(null);
		setPhase("pending");
		setJobId(null);
		setQuestion("");

		let newJobId: string;
		try {
			const r = await fetch(
				`/api/projects/${encodeURIComponent(projectId)}/renketsu/ask`,
				{
					method: "POST",
					credentials: "include",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						question: q,
						mode: "auto",
						prior_turns: nextPriorTurns,
					}),
				},
			);
			if (!r.ok) {
				setPhase(null);
				setError(`request failed: ${r.status}`);
				return;
			}
			const body = (await r.json()) as { job_id: string };
			newJobId = body.job_id;
			setJobId(newJobId);
		} catch (e) {
			setPhase(null);
			setError(e instanceof Error ? e.message : String(e));
			return;
		}

		const url = `/api/projects/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(newJobId)}/events`;
		const es = new EventSource(url, { withCredentials: true });
		sseRef.current = es;
		es.addEventListener("snapshot", (ev) => {
			try {
				const snap = JSON.parse((ev as MessageEvent).data) as JobSnapshot;
				if (
					snap.status === "completed" ||
					snap.status === "failed" ||
					snap.status === "cancelled"
				) {
					if (snap.status === "completed") {
						setResult((snap.result as AskResult) ?? null);
					} else if (snap.status === "cancelled") {
						setError("Cancelled");
					} else {
						setError(snap.error ?? "ask failed");
					}
					setPhase(null);
					es.close();
					sseRef.current = null;
				} else {
					setPhase(snap.progress?.phase ?? "pending");
				}
			} catch {
				// malformed frame; ignore
			}
		});
	}

	async function cancelRunning() {
		if (!projectId || !jobId) return;
		try {
			await fetch(
				`/api/projects/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(jobId)}`,
				{ method: "DELETE", credentials: "include" },
			);
			// The SSE channel will emit a `cancelled` snapshot; our
			// handler picks up the state. If the API replied 409 (already
			// complete), we let the natural completion flow happen.
		} catch {
			// best-effort; the worker checks isCancelled() periodically
		}
	}

	async function openStoredAsk(id: string) {
		if (!projectId) return;
		sseRef.current?.close();
		sseRef.current = null;
		setError(null);
		setPhase(null);
		try {
			const r = await fetch(
				`/api/projects/${encodeURIComponent(projectId)}/jobs/${encodeURIComponent(id)}`,
				{ credentials: "include" },
			);
			if (!r.ok) {
				setError(`could not load ask: ${r.status}`);
				return;
			}
			const body = (await r.json()) as { job: JobSnapshot };
			const j = body.job;
			if (j.status !== "completed") {
				setError(`ask is ${j.status}`);
				return;
			}
			setSubmitted(j.input?.question ?? "");
			setJobId(j.id);
			setResult((j.result as AskResult) ?? null);
		} catch (e) {
			setError(e instanceof Error ? e.message : String(e));
		}
	}

	if (!open) return null;

	const running = phase !== null && !result && !error;
	const markState: MarkState = running
		? phase === "composing"
			? "composing"
			: "thinking"
		: submitted
			? "idle"
			: "breathing";

	const canvas: CanvasPlan | null = result
		? validateCanvas(result.canvas) ?? defaultCanvas(result)
		: null;

	const reset = () => {
		sseRef.current?.close();
		sseRef.current = null;
		setSubmitted(null);
		setResult(null);
		setError(null);
		setPhase(null);
		setJobId(null);
		setQuestion("");
		setPriorTurns([]); // back-to-menu also drops the thread
		setInThreadFrom(null);
		setTimeout(() => inputRef.current?.focus(), 60);
	};

	/**
	 * Continue the thread (in-palette): folds the current Q+A into
	 * `priorTurns` and drops back to the empty composer, with the
	 * "Following up on" indicator visible. Same effect as opening the
	 * palette from the /ask page's Continue link, just without
	 * leaving the modal.
	 */
	function continueInPalette() {
		if (!submitted || !result?.answer) return;
		const nextPrior = [
			...priorTurns,
			{ question: submitted, answer: result.answer },
		].slice(-3);
		const branchedQuestion = submitted;
		sseRef.current?.close();
		sseRef.current = null;
		setPriorTurns(nextPrior);
		setInThreadFrom({ question: branchedQuestion });
		setSubmitted(null);
		setResult(null);
		setPhase(null);
		setJobId(null);
		setError(null);
		setQuestion("");
		setTimeout(() => inputRef.current?.focus(), 60);
	}

	return (
		<div
			className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] px-4"
			role="dialog"
			aria-modal="true"
			onClick={onClose}
		>
			<div
				className="absolute inset-0 bg-black/55 backdrop-blur-sm"
				aria-hidden="true"
			/>

			<div
				className="relative w-full max-w-[760px] max-h-[76vh] glass-hi rounded-[16px] overflow-hidden flex flex-col animate-palette-in"
				onClick={(e) => e.stopPropagation()}
			>
				{/* Header: composer OR question echo. The mark is the spinner
				    when running — no extra Loader2 here.
				    items-center on the composer row, items-start when the
				    echo has multiple stacked rows so the mark hugs the
				    QUESTION label. */}
				{submitted ? (
					<div className="shrink-0 px-4 py-3 border-b border-[var(--color-border)] flex items-start gap-3">
						{/* Back arrow doubles as the keyboard ESC affordance:
						    Esc once = back to main menu, Esc again = close.
						    Click does the same as the first Esc. */}
						<button
							type="button"
							onClick={reset}
							title="Back (Esc)"
							aria-label="Back to main menu"
							className="shrink-0 pill pill-icon !w-7 !h-7 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
						>
							<ArrowLeft className="w-3.5 h-3.5" />
						</button>
						{/* Mark + question text on one row, items-start so the
						    mark aligns with the question's first line rather
						    than floating above a meta label. The QUESTION
						    label is gone — users know what they typed. */}
						<div className="flex-1 min-w-0 flex items-start gap-3">
							<Mark
								state={markState}
								size={22}
								gradientId="mark-palette"
								className="shrink-0 mt-px"
							/>
							<div className="flex-1 min-w-0">
								<div className="text-[15px] text-[var(--color-text)] leading-[1.4]">
									{submitted}
								</div>
								{running && (
									<div className="text-[11px] text-[var(--color-text-muted)] mt-1">
										{PHASE_LABEL[phase ?? "pending"] ?? "Working"}
									</div>
								)}
							</div>
						</div>
						{running ? (
							<button
								type="button"
								onClick={cancelRunning}
								className="shrink-0 text-[11px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] transition-colors mt-1"
							>
								Cancel
							</button>
						) : result && jobId ? (
							<button
								type="button"
								onClick={expandToRoute}
								title="Expand to full view"
								aria-label="Expand to full view"
								className="shrink-0 pill pill-icon !w-7 !h-7 text-[var(--color-text-muted)] hover:text-[var(--color-text)]"
							>
								<Maximize2 className="w-3.5 h-3.5" />
							</button>
						) : null}
					</div>
				) : (
					<div className="shrink-0 px-4 py-3 border-b border-[var(--color-border)] flex items-center gap-3">
						<Mark
							state={markState}
							size={22}
							gradientId="mark-palette"
							className="shrink-0"
						/>
						<textarea
							ref={inputRef}
							value={question}
							onChange={(e) => setQuestion(e.target.value)}
							placeholder="Ask anything about your corpus"
							rows={1}
							className="composer-input flex-1 bg-transparent resize-none text-[15px] leading-[1.45] text-[var(--color-text)] placeholder:text-[var(--color-text-dim)] min-h-[22px] max-h-[140px] py-0"
							onKeyDown={(e) => {
								if (e.key === "Enter" && !e.shiftKey) {
									e.preventDefault();
									submit();
								}
							}}
						/>
					</div>
				)}

				{/* Body — recents / skeleton / canvas / error. While running
				    we render a skeleton of the final layout (sources strip
				    + prose lines + follow-ups) instead of going blank. The
				    header carries the textual phase; this telegraphs
				    structure. */}
				<div className="flex-1 overflow-y-auto px-4 py-4">
					{!submitted && (
						<div className="space-y-4">
							{inThreadFrom && (
								<BranchedFromIndicator
									question={inThreadFrom.question}
									onDismiss={() => {
										setInThreadFrom(null);
										setPriorTurns([]);
									}}
								/>
							)}
							<EmptyState
								recents={recents}
								onSuggest={(q) => {
									setQuestion(q);
									submit(q);
								}}
								onOpenStored={openStoredAsk}
							/>
						</div>
					)}
					{submitted && error && (
						<div className="text-[13px] text-[var(--color-danger)]">
							{error}
						</div>
					)}
					{submitted && !error && !canvas && <RunningSkeleton />}
					{submitted && canvas && (
						<>
							<Canvas
								plan={canvas}
								singleColumn
								onFollowup={(q) => submit(q)}
								onFocusNode={focusNodeInGraph}
							/>
							{!running && result && (
								<button
									type="button"
									onClick={continueInPalette}
									className="mt-5 flex items-center gap-2 text-[12px] text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors"
								>
									<CornerDownRight className="w-3.5 h-3.5" />
									<span>Continue the thread</span>
								</button>
							)}
						</>
					)}
				</div>

				{/* Footer hints — state-aware. Submit only applies when
				    the composer is active; once an answer is on screen,
				    you can't resubmit without "New ask" (no conversation
				    threading in this version). */}
				<div className="shrink-0 border-t border-[var(--color-border)] px-4 py-2 flex items-center gap-3 text-[10px] text-[var(--color-text-dim)] mono">
					{!submitted && (
						<span className="flex items-center gap-1">
							<Kbd>↩</Kbd>
							submit
						</span>
					)}
					<span className="flex items-center gap-1">
						<Kbd>esc</Kbd>
						close
					</span>
				</div>
			</div>
		</div>
	);
}

function BranchedFromIndicator({
	question,
	onDismiss,
}: {
	question: string;
	onDismiss?: () => void;
}) {
	return (
		<div className="flex items-start gap-2 px-3 py-2 rounded-[8px] bg-white/[0.03] border border-[var(--color-border)]">
			<CornerDownRight className="w-3 h-3 mt-[3px] text-[var(--color-text-dim)] shrink-0" />
			<div className="flex-1 min-w-0">
				<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
					Following up on
				</div>
				<div className="text-[12px] text-[var(--color-text-muted)] line-clamp-2 leading-[1.4]">
					{question}
				</div>
			</div>
			{onDismiss && (
				<button
					type="button"
					onClick={onDismiss}
					title="Drop thread context"
					aria-label="Drop thread context"
					className="shrink-0 text-[var(--color-text-dim)] hover:text-[var(--color-text)] transition-colors -mr-1 mt-px text-[14px] leading-none"
				>
					×
				</button>
			)}
		</div>
	);
}

function Kbd({ children }: { children: React.ReactNode }) {
	return (
		<kbd className="px-1 py-0.5 rounded-[3px] bg-white/[0.05] border border-[var(--color-border)] mr-0.5">
			{children}
		</kbd>
	);
}

/**
 * Layout-shaped placeholder shown while a canvas is in flight.
 * Telegraphs the final structure (sources strip, prose paragraphs,
 * follow-up pills) so the panel feels alive without duplicating the
 * textual phase indicator that already lives in the header.
 */
function RunningSkeleton() {
	return (
		<div className="space-y-5">
			{/* Sources strip skeleton — overflow-hidden (not auto): there's
			    nothing to scroll to, so a scrollbar would be visual noise.
			    The faded fourth card hints "more content" by clipping
			    into the edge instead. */}
			<div className="space-y-2">
				<div className="skeleton h-[10px] w-[48px]" />
				<div className="flex gap-2 overflow-hidden py-2 -my-2 -mx-2 px-2">
					<div className="skeleton-card shrink-0 w-[220px] h-[64px]" />
					<div className="skeleton-card shrink-0 w-[220px] h-[64px]" />
					<div className="skeleton-card shrink-0 w-[220px] h-[64px]" />
					<div className="skeleton-card shrink-0 w-[220px] h-[64px] opacity-60" />
				</div>
			</div>
			{/* Prose lines */}
			<div className="space-y-2.5">
				<div className="skeleton h-[12px] w-full" />
				<div className="skeleton h-[12px] w-full" />
				<div className="skeleton h-[12px] w-[88%]" />
				<div className="skeleton h-[12px] w-[72%]" />
			</div>
			<div className="space-y-2.5">
				<div className="skeleton h-[12px] w-full" />
				<div className="skeleton h-[12px] w-[64%]" />
			</div>
			{/* Follow-ups */}
			<div className="space-y-2">
				<div className="skeleton h-[10px] w-[52px]" />
				<div className="flex gap-2">
					<div className="skeleton h-[26px] w-[110px] rounded-full" />
					<div className="skeleton h-[26px] w-[140px] rounded-full" />
					<div className="skeleton h-[26px] w-[96px] rounded-full" />
				</div>
			</div>
		</div>
	);
}

interface EmptyStateProps {
	recents: RecentAsk[];
	onSuggest: (q: string) => void;
	onOpenStored: (id: string) => void;
}

function EmptyState({ recents, onSuggest, onOpenStored }: EmptyStateProps) {
	return (
		<div className="space-y-5">
			<div className="space-y-2">
				<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
					Try
				</div>
				<div className="flex flex-col">
					{SUGGESTIONS.map((s) => (
						<button
							key={s}
							type="button"
							onClick={() => onSuggest(s)}
							className="text-left px-2 py-1.5 -mx-2 rounded-[6px] text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04] transition-colors"
						>
							{s}
						</button>
					))}
				</div>
			</div>
			{recents.length > 0 && (
				<div className="space-y-2">
					<div className="text-[10px] uppercase tracking-wider text-[var(--color-text-dim)]">
						Recent
					</div>
					<div className="flex flex-col">
						{recents.map((r) => (
							<button
								key={r.id}
								type="button"
								onClick={() => onOpenStored(r.id)}
								className="text-left px-2 py-1.5 -mx-2 rounded-[6px] text-[13px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-white/[0.04] transition-colors flex items-center justify-between gap-3"
							>
								<span className="truncate flex-1">{r.question}</span>
								<span className="shrink-0 text-[10px] mono text-[var(--color-text-dim)]">
									{relativeTime(r.createdAt)}
								</span>
							</button>
						))}
					</div>
				</div>
			)}
		</div>
	);
}

function relativeTime(iso: string): string {
	if (!iso) return "";
	const t = Date.parse(iso);
	if (!Number.isFinite(t)) return "";
	const s = Math.floor((Date.now() - t) / 1000);
	if (s < 60) return "just now";
	if (s < 3600) return `${Math.floor(s / 60)}m`;
	if (s < 86400) return `${Math.floor(s / 3600)}h`;
	return `${Math.floor(s / 86400)}d`;
}
